/*eslint no-magic-numbers: ["error", { "ignore": [0, 1, 4, 5] }]*/
import React, { useEffect, useState, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'

import { SITE_STATUS_DRAFT_ID, SITE_STATUS_EDIT_ONLY_ID, SITE_STATUS_SUBMITTED_EDIT_ONLY_ID } from 'constants/siteStatuses'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import AssessmentTitle from 'components/AssessmentTitle'
import AddNewIcon from 'assets/icons/add-new'
import Accordion from 'components/Accordion'
import { congregationOptions } from 'constants/congregationOptions'
import Modal from 'components/Modal'
import FlowRequestModal from 'components/FlowRequestModal'
import CongregationContent from './CongregationContent'
import CongregationHeader from './CongregationHeader'
import {
  createCongregationsItem,
  getCongregationsList,
  removeSpeciesCongregation
} from 'api/congregations'
import { showErrorToast } from 'utils/toast'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { ProfileContext } from 'providers/profile'
import { useIBAAccess } from 'hooks/useIBAAccess'

import { AddNewCongregationBtnWrapper, CongregationListTitle, CongregationListTitleWrapper } from './style'
import PropTypes from 'prop-types'

const defaultCongregation = {
  CongregationGroupId: null,
  min: null,
  best: null,
  max: null,
  year_min: null,
  year_max: null,
  season_id: null,
  description: '',
  id: 'defaultTestId'
}

export const getAvailableCongregationsOptions = (list, options) => {
  return options.filter((option) => {
    return !list.some((element) => {
      return element.CongregationGroupId === option.value
    })
  })
}

const CongregationsList = ({ site, setSiteProposalFieldValidation }) => {
  const { profile } = useContext(ProfileContext)
  const isAbleToAdd = site.SiteStatusId === SITE_STATUS_DRAFT_ID
  const params = useParams()
  const [congregationList, setCongregationList] = useState([])
  const [availableCongregationsOptions, setAvailableCongregationOptions] = useState([])
  const [itemToRemove, setItemToRemove] = useState(false)

  const editOnlySiteStatus = site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  
  // Check if user has IBA access for this site
  const { canSeeSiteIBACongregations } = useIBAAccess()

  // Don't render congregations if user doesn't have IBA access for this site
  if (!canSeeSiteIBACongregations(site)) {
    return null
  }

  const getSpeciesCongregationList = () => {
    try {
      getCongregationsList(site.id, editOnlySiteStatus ? site.sitehistoryId : params.siteHistoryId).then((data) => {
        if (data.error) {
          return showErrorToast(data.error)
        }
        // Sort congregations by ID to maintain consistent order
        const sortedData = data.sort((a, b) => a.id.localeCompare(b.id))
        setCongregationList(sortedData)
        setAvailableCongregationOptions(getAvailableCongregationsOptions(sortedData, congregationOptions))
      })
    } catch (e) {
      showErrorToast(e)
    }

  }

  const updateCongregationList = (congregation) => {
    const listCopy = [...congregationList]
    const foundIndex = listCopy.findIndex((item) => {
      return item.id === congregation.id
    })
    listCopy[foundIndex] = { ...congregation }
    // Sort the updated list by ID to maintain consistent order
    const sortedList = listCopy.toSorted((a, b) => a.id.localeCompare(b.id))
    setCongregationList(sortedList)
  }

  const createCongregations = (id) => {
    try {
      createCongregationsItem(site.id, id, params.siteHistoryId).then((data) => {
        if (data.error) {
          return showErrorToast(data.error)
        }
        const newList = [...congregationList, data].filter((element) => {
          return element.id !== 'defaultTestId'
        })

        // Sort the new list by ID to maintain consistent order
        const sortedNewList = newList.toSorted((a, b) => a.id.localeCompare(b.id))
        setCongregationList(sortedNewList)
        setAvailableCongregationOptions(getAvailableCongregationsOptions(sortedNewList, congregationOptions))
      })
    } catch (e) {
      showErrorToast(e)
    }
  }

  const addCongregations = () => {
    setSiteProposalFieldValidation(defaultSiteProposalValidationResults)
    const isDefaultCongregationPresent = congregationList.some((element) => {
      return element.id === 'defaultTestId'
    })
    if (isDefaultCongregationPresent || congregationList.length > 4) {
      return
    }
    setCongregationList((s) => {
      return [...s, { ...defaultCongregation }]
    })
  }

  const removeCongregations = (item) => {
    setSiteProposalFieldValidation(defaultSiteProposalValidationResults)
    if (item.id === 'defaultTestId') {
      const newList = [...congregationList]
      const indexToRemove = congregationList.findIndex((element) => {
        return element.id === item.id
      })

      newList.splice(indexToRemove, 1)
      setAvailableCongregationOptions(getAvailableCongregationsOptions(newList, congregationOptions))
      setCongregationList(newList)
      setItemToRemove(false)
      return
    }

    try {
      removeSpeciesCongregation(site.id, item.id, params.siteHistoryId, item.historyId).then((data) => {
        if (data.error) {
          return showErrorToast(data.error)
        }
        setItemToRemove(false)
        getSpeciesCongregationList()
      })
    } catch (err) {
      showErrorToast(err)
    }
  }

  const openRemoveModal = (item) => {
    setItemToRemove(item)
  }

  const closeRemoveModal = () => {
    setItemToRemove(false)
  }


  useEffect(() => {
    getSpeciesCongregationList()
  }, [])

  const oldCongregationList = congregationList.filter((element) => {
    return element.lastReassessmentDate
  })

  const newCongregationList = congregationList.filter((element) => {
    return !element.lastReassessmentDate
  })

  if (site.SiteStatusId !== SITE_STATUS_DRAFT_ID && congregationList.length === 0) {
    return null
  }

  return (
    <Container>
      <AssessmentTitle
        titleId='congregationTitle'
        subTitleId='congregationSubTitle'
        fontSizePageSubtitle='16px'
        marginTop='96px'
        marginBottom='96px'
      />
      <Row>
        <Col md={8} mdOffset={2} dataTest='congregation-form'>
          {(congregationList.length === 0) ? (
              <CongregationContent
                item={defaultCongregation}
                createCongregations={createCongregations}
                options={availableCongregationsOptions}
                site={site}
                params={params}
                defaultItem={true}
              />
          ) : (
            <>
              {site.SiteStatusId === SITE_STATUS_DRAFT_ID ? (
                <>
                  {oldCongregationList.length > 0 && (
                    <>
                      <Row>
                        <Col md={8} mdOffset={2}>
                          <CongregationListTitleWrapper>
                            <CongregationListTitle>
                              <FormattedMessage
                                id='existingTriggers' />
                            </CongregationListTitle>
                          </CongregationListTitleWrapper>
                        </Col>
                      </Row>
                      <Accordion
                        items={oldCongregationList}
                        contentProps={{
                          updateCongregationList,
                          createCongregations,
                          options: availableCongregationsOptions,
                          site,
                          params,
                          defaultItem: false,
                          getSpeciesCongregationList
                        }}
                        headerProps={{ openRemoveModal, site, newTriggers: false, oldTriggers: true }}
                        contentComponent={CongregationContent}
                        headerComponent={CongregationHeader}
                        initialActive={null}
                      />
                    </>
                  )}
                  {newCongregationList.length > 0 && (
                    <>
                      <Row>
                        <Col md={8} mdOffset={2}>
                          <CongregationListTitleWrapper addMarginTop={oldCongregationList.length > 0}>
                            <CongregationListTitle>
                              <FormattedMessage
                                id='newTriggers' />
                            </CongregationListTitle>
                          </CongregationListTitleWrapper>
                        </Col>
                      </Row>
                      <Accordion
                        items={newCongregationList}
                        contentProps={{
                          updateCongregationList,
                          createCongregations,
                          options: availableCongregationsOptions,
                          site,
                          params,
                          defaultItem: false,
                          getSpeciesCongregationList
                        }}
                        headerProps={{ openRemoveModal, site, newTriggers: true, oldTriggers: false }}
                        contentComponent={CongregationContent}
                        headerComponent={CongregationHeader}
                      />
                    </>
                  )}
                </>
              ) : (
                <Accordion
                  items={congregationList}
                  contentProps={{
                    updateCongregationList,
                    createCongregations,
                    options: availableCongregationsOptions,
                    site,
                    params,
                    defaultItem: false,
                    getSpeciesCongregationList
                  }}
                  headerProps={{ openRemoveModal, site, newTriggers: false, oldTriggers: false }}
                  contentComponent={CongregationContent}
                  headerComponent={CongregationHeader}
                />
              )}
            </>
          )}
        </Col>
      </Row>
      {(isAbleToAdd && congregationList.length < 5 && congregationList.length !== 0) && (
        <Row>
          <Col md={8} mdOffset={2}>
            <AddNewCongregationBtnWrapper onClick={addCongregations}>
              <AddNewIcon />
              <FormattedMessage id='addNewCongregation' />
            </AddNewCongregationBtnWrapper>
          </Col>
        </Row>
      )}
      <Modal
        width='416px'
        isOpen={itemToRemove}
        toggleModal={closeRemoveModal}
        modalTitleId='removeCongregations'
        bottomBorder={true}
      >
        <FlowRequestModal
          cancelHandler={closeRemoveModal}
          item={itemToRemove}
          confirmBtnText='confirm'
          text={<FormattedMessage id='removeCongregationModalBody' values={{ name: <b>{itemToRemove.name}</b> }} />}
          confirmHandler={removeCongregations}
          rejectBtnText='cancel'
        />
      </Modal>
    </Container>
  )
}
CongregationsList.propTypes = {
  site: PropTypes.object.isRequired,
  setSiteProposalFieldValidation: PropTypes.func.isRequired
}

export default CongregationsList
